import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card , fmtBRL} from "@atlas/ui/web";
import { condicoesPeloCet, montarOpcoesDeCredito } from "@atlas/domain";
import { atlas } from "../../lib/sdk";
import type { MatriculaInfo } from "../../lib/matricula-data";
import {
  formatRemaining,
  getActiveLock,
  setLock,
  clearLock,
  SIMULATION_LOCK_KEY,
  type LockProduto,
} from "../../lib/simulation-lock";

// Teto default enquanto nao ha oferta carregada. Cliente pediu 30/07/2026
// que a lista deixe de ser fixa (12, 24, ...) e vire "1..N" onde N vem do
// teto da prefeitura (convenio.maxParcelas) e do prazoMaxMeses da tabela
// do banco. O servidor escolhe qualquer numero de parcelas 1..N.
const PARCELAS_DEFAULT_MAX = 120;



/** Simulador de credito reaproveitavel. Renderiza sem header — quem usa (a
 *  pagina /servidor/simular OU a pagina MarketPlace) coloca o titulo em volta.
 *  Lida com lock de 48h, calculo Price e navegacao pro /termo pra assinar.
 *
 *  A prop `compact` esconde o card "Sua margem para emprestimo" (que ja aparece
 *  em outra parte da tela onde o simulador esta embutido). */
type ModalidadeSim = "emprestimo" | "cartao_consignado" | "cartao_beneficio";

/** Mapeia o produto interno da UI pro produto do lock. Sao margens independentes,
 *  entao uma trava em EMPRESTIMO nao bloqueia CARTAO_CONSIGNADO nem vice-versa. */
function lockProdutoDe(p: ModalidadeSim): LockProduto {
  if (p === "cartao_consignado") return "CARTAO_CONSIGNADO";
  if (p === "cartao_beneficio") return "CARTAO_BENEFICIOS";
  return "EMPRESTIMO";
}

/** Verifica se uma proposta pertence ao produto travado. Prefer tipoMargem
 *  (fonte da verdade — CARTAO_CONSIGNADO vs CARTAO_BENEFICIOS sao ambos
 *  tipoContrato=ECONSIGNADO, so o tipoMargem distingue). Se tipoMargem nao
 *  vier (dados antigos), cai no tipoContrato como fallback.
 *
 *  - EMPRESTIMO trava: solta com qualquer proposta de credito (emprestimo/refin).
 *  - CARTAO_CONSIGNADO trava: solta so com proposta de cartao consignado.
 *  - CARTAO_BENEFICIOS trava: solta so com proposta de cartao beneficio. */
function matchesLockProduto(tipoContrato: string | undefined, lockProd: LockProduto, tipoMargem?: string): boolean {
  // Se tipoMargem foi informado pelo backend, ele e definitivo.
  if (tipoMargem) return tipoMargem === lockProd;
  // Fallback pra propostas antigas sem tipoMargem persistido.
  const t = (tipoContrato ?? "").toUpperCase();
  if (lockProd === "EMPRESTIMO") {
    if (!t) return true;
    return t === "EMPRESTIMO" || t === "REFIN";
  }
  if (lockProd === "CARTAO_CONSIGNADO") {
    // Sem tipoMargem, ECONSIGNADO por default e cartao consignado. Beneficio
    // "antigo" fica identificado como consignado (pouco impacto — dados velhos).
    return t === "ECONSIGNADO";
  }
  // CARTAO_BENEFICIOS — sem tipoMargem nao da pra distinguir, so por heuristica
  // frouxa no nome que nao vale a pena (retorna false — errar pra menos).
  return false;
}

export function SimuladorInline({
  info,
  taxaAmDefault = 0.0179,
  valorDefault = 8500,
  parcelasDefault = 36,
  compact = false,
  produto = "emprestimo",
  ofertaId,
  bancoOferta,
  maxParcelasOferta,
  maxValorOferta,
}: {
  info: MatriculaInfo | null;
  taxaAmDefault?: number;
  valorDefault?: number;
  parcelasDefault?: number;
  compact?: boolean;
  /** Produto do simulador. Sem tabs — a tela que embute o SimuladorInline decide
   *  qual mostrar (dashboard aponta cada botao "Simular" pra tela dedicada). */
  produto?: ModalidadeSim;
  /** Oferta de marketing quando o simulador foi aberto a partir de um card
   *  do marketplace. Ativa HARD CAPS INDEPENDENTES do prazoMax da tabela do
   *  banco. Enviado no POST /me/propostas pra o backend re-validar. */
  ofertaId?: string;
  /** Nome do banco dono da oferta de marketing. Quando presente, a escolha da
   *  oferta vencedora fica restrita as tabelas DESTE banco — a promocao e' dele,
   *  e a proposta tem que sair pra ele. */
  bancoOferta?: string;
  maxParcelasOferta?: number;
  maxValorOferta?: number;
}) {
  const nav = useNavigate();
  const lockProduto = lockProdutoDe(produto);
  const [valor, setValor] = useState<number>(valorDefault);
  const [parcelas, setParcelas] = useState<number>(parcelasDefault);

  // Ofertas ativas do convenio dessa matricula — traz o prazoMaxMeses de cada
  // tabela publicada pelo(s) banco(s). Poll 5s = "tempo real": quando o banco
  // altera o prazo max no /banco/cadastros/tabela-emprestimos, o servidor ve
  // as parcelas disponiveis mudarem sem refresh. Contratos ja vigentes nao
  // sao afetados — a tabela e' consultada so pra NOVAS simulacoes.
  //
  // queryKey precisa comecar com ["servidor", "ofertas"] pra bater com o
  // invalidateQueries do form do banco (upsertTabela) — assim a mudanca
  // reflete IMEDIATAMENTE, sem esperar os 5s do poll. refetchIntervalInBackground
  // garante o poll rodar mesmo com aba fora de foco.
  const ofertasQ = useQuery({
    queryKey: ["servidor", "ofertas", info?.matricula],
    queryFn: () => atlas.servidor.ofertas(info?.matricula),
    enabled: !!info?.matricula,
    refetchInterval: 3_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: 0, // forca sempre buscar dado fresco quando invalidado
  });
  const margemEmprestimo = useMemo(() => {
    if (!info) return 0;
    const t = info.margem.margens_por_tipo.find((m) => m.tipo === "EMPRESTIMO");
    return t?.disponivel ?? info.margem.margem.disponivel ?? 0;
  }, [info]);

  /**
   * A VITRINE: um card por banco, com as combinacoes de prazo que cabem na
   * margem desta matricula.
   *
   * Ate 20/08/2026 este bloco escolhia sozinho uma "oferta vencedora" (a de
   * menor taxa) e descartava o resto — o servidor via um numero, sem saber que
   * havia alternativas. Na pratica nem havia: a taxa era a do CONVENIO, igual
   * pra todos os bancos, e as tabelas eram obrigadas a copia-la. O simulador
   * cotava pelo TETO da prefeitura e chamava aquilo de oferta.
   *
   * Agora cada tabela tem preco e faixa de prazo proprios, entao a comparacao
   * passa a existir de verdade e vai pra tela. A margem entra como filtro, nao
   * como recusa no fim: prazo cuja parcela estoura a folha nao e' oferecido.
   *
   * A regra mora em @atlas/domain (montarOpcoesDeCredito) porque a API refaz a
   * mesma conta ao receber a proposta — o navegador nao decide preco sozinho.
   */
  const vitrine = useMemo(() => {
    const todas = ofertasQ.data?.ofertas ?? [];
    // Fluxo vindo de um card de oferta de marketing: so o banco que publicou a
    // promocao entra. Se ele nao tem tabela vigente neste convenio, nao ha com
    // quem contratar — melhor bloquear (botao desabilita via bancoEscolhido
    // vazio) do que mandar a proposta pro banco errado.
    const elegiveis = bancoOferta ? todas.filter((o) => o.bancoNome === bancoOferta) : todas;
    const tetoParcelas = Math.min(
      ...elegiveis.map((o) => o.tetoParcelas ?? PARCELAS_DEFAULT_MAX),
      // Oferta de marketing traz teto proprio, INDEPENDENTE do prazo da tabela
      // (cliente 30/07/2026) — continua valendo, agora como corte da vitrine.
      maxParcelasOferta ?? PARCELAS_DEFAULT_MAX,
    );
    return montarOpcoesDeCredito({
      tabelas: elegiveis.map((o) => ({
        id: o.id,
        nome: o.nome ?? undefined,
        bancoId: o.bancoId,
        bancoNome: o.bancoNome,
        convenioId: o.convenioId,
        convenio: o.convenio,
        taxaAm: o.taxaAm,
        prazoMinMeses: o.prazoMinMeses,
        prazoMaxMeses: o.prazoMaxMeses,
      })),
      valor,
      margem: margemEmprestimo,
      tetoParcelas: Number.isFinite(tetoParcelas) ? tetoParcelas : PARCELAS_DEFAULT_MAX,
    });
  }, [ofertasQ.data, bancoOferta, valor, margemEmprestimo, maxParcelasOferta]);

  const comOpcoes = useMemo(() => vitrine.filter((c) => c.opcoes.length > 0), [vitrine]);
  const [bancoEscolhidoNome, setBancoEscolhidoNome] = useState<string | null>(null);
  // Segue o melhor preco enquanto o servidor nao escolher, e volta pra ele se o
  // banco escolhido sair da vitrine (valor mudou e a parcela nao cabe mais).
  const cardEscolhido =
    comOpcoes.find((c) => c.bancoNome === bancoEscolhidoNome) ?? comOpcoes[0] ?? null;

  // Prazos disponiveis SAO os do banco escolhido — nao mais 1..N linear. Duas
  // tabelas podem deixar buracos (1..12 e 25..36), e oferecer 18x ali seria
  // oferecer o que o banco nao publicou.
  const opcoesDoPrazo = cardEscolhido?.opcoes ?? [];
  const opcaoAtual =
    opcoesDoPrazo.find((o) => o.parcelas === parcelas) ?? opcoesDoPrazo[opcoesDoPrazo.length - 1] ?? null;

  // Se o prazo escolhido nao esta mais disponivel (mudou o valor, o banco, ou
  // a tabela saiu do ar), cai no mais longo viavel — o de menor parcela.
  useEffect(() => {
    if (opcoesDoPrazo.length === 0) return;
    if (!opcoesDoPrazo.some((o) => o.parcelas === parcelas)) {
      setParcelas(opcoesDoPrazo[opcoesDoPrazo.length - 1]!.parcelas);
    }
  }, [opcoesDoPrazo, parcelas]);

  // A taxa acompanha o PRAZO escolhido: e' o ponto da mudanca. 30x pode custar
  // 3% e 90x custar 5% no mesmo banco.
  //
  // Cliente pediu 30/07/2026: quando o servidor CLICOU numa OFERTA de marketing
  // (ofertaId na URL), a taxa vem DA OFERTA (taxaAmDefault via ?taxa=X), nao da
  // tabela — assim o numero bate com o que o card prometeu.
  const taxaAm: number | null = ofertaId != null && taxaAmDefault > 0
    ? taxaAmDefault
    : (opcaoAtual?.taxaAm ?? null);
  const bancoVigente = cardEscolhido?.bancoNome ?? "";
  const parcelasMax = opcoesDoPrazo.length > 0 ? opcoesDoPrazo[opcoesDoPrazo.length - 1]!.parcelas : PARCELAS_DEFAULT_MAX;
  const parcelasMaxTabela = parcelasMax;

  const [clientLockExpiresAt, setClientLockExpiresAt] = useState<number | null>(() =>
    getActiveLock(info?.idMatricula ?? null, lockProduto),
  );
  const [now, setNow] = useState<number>(() => Date.now());

  // Re-le lock quando a matricula, o produto ou o lock em outra aba mudar.
  useEffect(() => {
    setClientLockExpiresAt(getActiveLock(info?.idMatricula ?? null, lockProduto));
    const onStorage = (e: StorageEvent) => {
      if (e.key === SIMULATION_LOCK_KEY) {
        setClientLockExpiresAt(getActiveLock(info?.idMatricula ?? null, lockProduto));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [info?.idMatricula, lockProduto]);

  // Poll de propostas SEMPRE (nao so quando ja ha lock local) — a trava tem
  // que ser derivada da fonte da verdade (backend), nao so do localStorage.
  // Se o usuario limpou cache / trocou navegador, o localStorage nao tem lock,
  // mas o backend TEM a proposta pendente e ela precisa continuar travando.
  const propostasQ = useQuery({
    queryKey: ["servidor", "propostas", info?.matricula ?? null],
    queryFn: () => atlas.servidor.propostas(info?.matricula),
    refetchInterval: 10_000,
    enabled: !!info,
  });

  // "DD/MM/YYYY" -> timestamp (fim do dia local, 23:59:59). Retorna null se
  // string for invalida.
  const parseBRDate = (s: string | undefined | null): number | null => {
    if (!s) return null;
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
    if (!m) return null;
    const t = new Date(`${m[3]}-${m[2]}-${m[1]}T23:59:59`).getTime();
    return Number.isFinite(t) ? t : null;
  };

  // Proposta ainda pendente de decisao do banco (Aguardando/Analise/qualquer
  // situacao que NAO seja aceite/recusa explicita).
  const propostaPendente = useMemo(() => {
    const relevantes = (propostasQ.data?.propostas ?? [])
      .filter((p) => matchesLockProduto(p.tipoContrato, lockProduto, p.tipoMargem));
    return relevantes.find((p) => {
      const s = p.situacao.toLowerCase();
      const decidiu =
        s.includes("ativo") || s.includes("aprov") || s.includes("averb") || s.includes("formaliz") || s.includes("quitad") ||
        s.includes("cancel") || s.includes("recus") || s.includes("reprov") || s.includes("rejeit") || s.includes("negad") || s.includes("estorn") ||
        // Trava expirou (48h da reserva sem resposta do banco) -> proposta esta
        // encerrada, libera o servidor pra criar uma nova. Cliente 31/07/2026.
        s.includes("expir");
      return !decidiu;
    });
  }, [propostasQ.data, lockProduto]);

  // Lock derivado do backend: expira em `expira_em` (48h da criacao, ja
  // computado no backend) ou fallback pra `data` + 48h.
  const serverLockExpiresAt = useMemo(() => {
    if (!propostaPendente) return null;
    // Prefer ISO exatos (com hora/min/seg) — parse de DD/MM/YYYY perdia precisao
    // e caia em fim-de-dia (23:59:59), inflando a contagem em ate ~24h.
    const isoExp = propostaPendente.expira_em_iso ? Date.parse(propostaPendente.expira_em_iso) : NaN;
    if (Number.isFinite(isoExp)) return isoExp;
    const isoCreated = propostaPendente.criado_em_iso ? Date.parse(propostaPendente.criado_em_iso) : NaN;
    if (Number.isFinite(isoCreated)) return isoCreated + 48 * 60 * 60 * 1000;
    // Fallback pra propostas antigas sem ISO (dado persistido antes desta mudanca).
    const exp = parseBRDate(propostaPendente.expira_em);
    if (exp) return exp;
    const created = parseBRDate(propostaPendente.data);
    return created ? created + 48 * 60 * 60 * 1000 : null;
  }, [propostaPendente]);

  // Lock efetivo: pega o MAIOR entre localStorage e backend (mais conservador).
  // Se so um dos dois tiver valor, usa aquele.
  const lockExpiresAt = useMemo(() => {
    if (clientLockExpiresAt && serverLockExpiresAt) return Math.max(clientLockExpiresAt, serverLockExpiresAt);
    return clientLockExpiresAt ?? serverLockExpiresAt;
  }, [clientLockExpiresAt, serverLockExpiresAt]);

  // Tick 1s enquanto ha lock ativo (pra atualizar o contador).
  useEffect(() => {
    if (!lockExpiresAt) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [lockExpiresAt]);

  // Se o banco decidiu (aceite/recusa), limpa localStorage tambem — nao vai
  // ter mais propostaPendente entao serverLockExpiresAt ja e null. Efeito
  // so pra sujar o localStorage tambem.
  useEffect(() => {
    if (!propostasQ.data || !clientLockExpiresAt) return;
    if (!propostaPendente) {
      const id = info?.idMatricula;
      if (id) clearLock(id, lockProduto);
      setClientLockExpiresAt(null);
    }
  }, [propostasQ.data, propostaPendente, clientLockExpiresAt, info?.idMatricula, lockProduto]);

  /**
   * Teto do slider: o maior valor que ALGUM banco da vitrine consegue caber na
   * margem — nao o maior que cabe no prazo atualmente selecionado.
   *
   * A diferenca importa desde que a tela virou comparacao. Com o teto preso ao
   * prazo corrente, pedir um valor alto reduzia o pedido sozinho e todos os
   * bancos voltavam a caber: nunca havia contraste. Agora o servidor pede ate o
   * limite real do mercado dele, e quem nao alcanca aquele valor aparece no card
   * dizendo ate quanto faz — que e' a informacao util.
   *
   * `valorMaximo` ja vem de cada card (montarOpcoesDeCredito), calculado pelo
   * inverso da Price no melhor arranjo daquele banco.
   */
  const maxValor = useMemo(() => {
    if (margemEmprestimo <= 0) return 500;
    // Vitrine vazia = ofertas ainda carregando, ou nenhum banco publicou tabela.
    // Sem isto o teto cairia pro piso de R$ 500 durante o carregamento e o
    // efeito de clamp logo abaixo reduziria o valor digitado sozinho. Enquanto
    // nao ha banco, o botao "Solicitar" ja fica bloqueado por bancoVigente.
    if (vitrine.length === 0) return Math.max(500, Math.floor((margemEmprestimo * PARCELAS_DEFAULT_MAX) / 100) * 100);
    const bruto = vitrine.reduce((maior, c) => Math.max(maior, c.valorMaximo), 0);
    const porMargem = Math.max(500, Math.floor(bruto / 100) * 100);
    // Quando o servidor CLICOU numa OFERTA de marketing, aplica valorMax
    // da oferta como HARD CAP. Cliente 30/07/2026: promocao e A PARTE do
    // simulador de partida — so cai aqui quando veio via URL.
    return maxValorOferta != null ? Math.min(porMargem, Math.floor(maxValorOferta / 100) * 100) : porMargem;
  }, [margemEmprestimo, vitrine, maxValorOferta]);

  useEffect(() => {
    if (valor > maxValor) setValor(maxValor);
  }, [maxValor, valor]);

  // CET calculado pelo helper compartilhado (@atlas/domain) — Newton-Raphson
  // pra IRR, mesma implementacao usada no backend. Cliente reportou 22/07/2026
  // que o CET aparecia MENOR que a taxa nominal (0.59% < 1.00%), impossivel:
  // era formula caseira `(total/liquido)^(1/parcelas)-1` que so calcula taxa
  // media incorreta. CET real >= taxaNominal porque inclui IOF+tarifas.
  //
  // Spec 014 (29/09/2026): a taxa publicada pelo banco E' o CET. A parcela sai
  // dele, sem IOF somado — somar de novo contava o imposto duas vezes e mostrava
  // ao servidor um CET maior que o do banco (1,79% publicado virava ~1,9%).
  //
  // Se taxaAm e null (nao ha oferta), zera tudo — a UI mostra "—" nas metricas.
  const { parcela, cetAa, totalPago: total } = useMemo(() => {
    if (!taxaAm || valor <= 0 || parcelas <= 0 || taxaAm <= 0) {
      return { parcela: 0, cetAa: 0, totalPago: 0 };
    }
    return condicoesPeloCet({ valorLiquido: valor, parcelas, cetAm: taxaAm });
  }, [valor, parcelas, taxaAm]);

  const excedeMargem = info ? parcela > margemEmprestimo : false;
  const locked = !!lockExpiresAt && lockExpiresAt > now;
  // Bloqueia solicitacao se nao ha banco/oferta ativa (nada de placeholder).
  const podeSolicitar = !!info && !excedeMargem && valor > 0 && parcelas > 0 && !locked && !!bancoVigente;

  function solicitar() {
    if (!podeSolicitar || !info) return;
    setLock(info.idMatricula, lockProduto);
    setClientLockExpiresAt(Date.now() + 48 * 60 * 60 * 1000);
    const params = new URLSearchParams({
      tipo: "novo",
      banco: bancoVigente, // banco real da oferta vencedora (rotulo do termo)
      // Identidade da oferta aceita — o /termo repassa pro backend, que roteia
      // o contrato por ela. Sem isto o banco exibido na tela e o banco que
      // recebe a proposta podem ser dois bancos diferentes.
      convenio: cardEscolhido?.convenioId ?? "",
      valor: String(Math.round(valor)),
      parcelas: String(parcelas),
      parcela: parcela.toFixed(2),
      taxaAm: ((taxaAm ?? 0) * 100).toFixed(2),
    });
    // Propaga a oferta pro /termo SO quando o servidor clicou na oferta
    // (ofertaId veio na URL). O simulador de partida nao carrega ofertaId.
    if (ofertaId) params.set("oferta", ofertaId);
    nav(`/servidor/termo?${params.toString()}`);
  }

  // Lock ativo — mostra o contador (mesmo layout, mais compacto quando embutido).
  if (locked && lockExpiresAt) {
    const restante = formatRemaining(lockExpiresAt - now);
    const expiraEmFormatado = new Date(lockExpiresAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    return (
      <Card style={{ padding: compact ? 20 : 28, textAlign: "center" }}>
        <div
          style={{
            width: 56, height: 56, borderRadius: "50%",
            background: "color-mix(in srgb, var(--gold-500) 20%, transparent)",
            color: "var(--gold-500)",
            display: "grid", placeItems: "center",
            fontSize: 28, margin: "0 auto 10px",
          }}
        >
          ⏳
        </div>
        <h3 style={{ margin: "0 0 6px" }}>Margem em pré-reserva</h3>
        <p style={{ color: "var(--text-muted)", margin: 0, fontSize: ".9rem", maxWidth: 460, marginInline: "auto" }}>
          {info ? (
            <>Sua margem da matrícula <b>{info.matricula}</b> está travada por 48h após a última simulação.</>
          ) : (
            <>Sua margem está travada por 48h após a última simulação.</>
          )}
        </p>
        <div
          style={{
            marginTop: 20, padding: "16px 20px", borderRadius: 12,
            background: "var(--bg-elev-2)", border: "1px solid var(--border-strong)",
            display: "inline-block", minWidth: 240,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            Tempo restante
          </div>
          <div style={{ marginTop: 4, fontSize: "2rem", fontWeight: 800, fontFamily: "var(--font-mono)", color: "var(--accent)", letterSpacing: "0.02em" }}>
            {restante}
          </div>
          <div style={{ marginTop: 4, fontSize: ".78rem", color: "var(--text-dim)" }}>
            Libera em {expiraEmFormatado}
          </div>
        </div>
        {/* Igual ao app: leva pra tela de Contratos, onde faz o acompanhamento. */}
        <div style={{ marginTop: 20 }}>
          <Button onClick={() => nav("/servidor/contratos")}>Acompanhar análise →</Button>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {produto !== "emprestimo" ? (
        <SimuladorCartao info={info} produto={produto} />
      ) : (
        <>
      {!compact && info ? (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>
                Sua margem para empréstimo
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "var(--emerald-500)", marginTop: 4 }}>
                {fmtBRL(margemEmprestimo)}
              </div>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", maxWidth: 320, textAlign: "right" }}>
              Sua parcela mensal não pode ultrapassar essa margem disponível na folha.
            </div>
          </div>
        </Card>
      ) : null}

      <Card>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>
            Valor solicitado
          </span>
          <div style={{ fontSize: 28, fontWeight: 800, color: "var(--accent)" }}>{fmtBRL(valor)}</div>
          {/* Input digitavel — no mobile o slider nao consegue definir valor
              inteiro certinho. Commit no blur/Enter. */}
          <ValorInput
            valor={valor}
            min={500}
            max={Math.max(maxValor, 1000)}
            onChange={setValor}
          />
          <input
            type="range"
            min={500}
            max={Math.max(maxValor, 1000)}
            step={100}
            value={Math.min(valor, maxValor)}
            onChange={(e) => setValor(Number(e.target.value))}
            style={{ width: "100%", accentColor: "var(--accent)" }}
          />
          {/* O teto e' do MERCADO desta matricula (melhor arranjo entre os
              bancos), nao do prazo selecionado — ver maxValor. Dizer "p/ Nx"
              aqui daria a entender que trocar o prazo mudaria o limite. */}
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Máximo com sua margem: <b>{fmtBRL(maxValor)}</b>
          </span>
        </div>
      </Card>

      {/* VITRINE — um card por banco. Substituiu o dropdown "1..N parcelas" com
          taxa unica: com preco por faixa de prazo, a escolha do servidor deixou
          de ser so "em quantas vezes" e passou a ser "com quem, e em quantas
          vezes". Cliente 20/08/2026. */}
      <Card>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 12 }}>
          Escolha a oferta
        </div>

        {ofertasQ.isPending ? (
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Buscando ofertas das instituições…</div>
        ) : vitrine.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Nenhuma instituição publicou tabela de crédito pra este convênio ainda.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {vitrine.map((card) => {
              const semOpcoes = card.opcoes.length === 0;
              const selecionado = !semOpcoes && card.bancoNome === bancoVigente;
              return (
                <div
                  key={`${card.bancoId ?? card.bancoNome}`}
                  onClick={() => { if (!semOpcoes) setBancoEscolhidoNome(card.bancoNome); }}
                  style={{
                    padding: "14px 16px",
                    borderRadius: 12,
                    border: `1px solid ${selecionado ? "var(--accent)" : "var(--border-strong)"}`,
                    background: selecionado ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "var(--bg-elev-2)",
                    opacity: semOpcoes ? 0.6 : 1,
                    cursor: semOpcoes ? "default" : "pointer",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
                    <b style={{ fontSize: 15 }}>{card.bancoNome}</b>
                    {semOpcoes ? (
                      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                        {card.valorMaximo > 0
                          ? <>faz até <b>{fmtBRL(Math.floor(card.valorMaximo / 100) * 100)}</b> com sua margem</>
                          : "sem margem disponível"}
                      </span>
                    ) : (
                      <span style={{ fontSize: 13, color: "var(--emerald-500)", fontWeight: 700 }}>
                        a partir de {(card.taxaAmMin * 100).toFixed(2)}% a.m.
                      </span>
                    )}
                  </div>
                  {!semOpcoes ? (
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                      {card.parcelasMin}× a {card.parcelasMax}× · parcela de {fmtBRL(card.parcelaMin)} a {fmtBRL(card.parcelaMax)}
                      {card.prazosForaDaMargem > 0 ? (
                        <span style={{ color: "var(--gold-500)" }}>
                          {" "}· {card.prazosForaDaMargem} prazo{card.prazosForaDaMargem > 1 ? "s" : ""} fora da sua margem
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {/* Prazos DO BANCO escolhido. Cada um mostra a taxa da faixa — e' onde
            "3% em 30x, 5% em 90x" fica visível pro servidor. */}
        {opcoesDoPrazo.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 8 }}>
              Em quantas parcelas
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <select
                value={parcelas}
                onChange={(e) => setParcelas(Number(e.target.value))}
                aria-label="Quantidade de parcelas"
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "1px solid var(--border-strong)",
                  background: "var(--bg-elev-2)",
                  color: "var(--text)",
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  minWidth: 260,
                }}
              >
                {opcoesDoPrazo.map((o) => (
                  <option key={o.parcelas} value={o.parcelas}>
                    {o.parcelas}× de {fmtBRL(o.parcela)} · CET {(o.taxaAm * 100).toFixed(2)}% a.m.
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {opcaoAtual ? <>Tabela <b>{opcaoAtual.tabelaNome}</b></> : null}
                {maxParcelasOferta != null && maxParcelasOferta <= parcelasMaxTabela ? (
                  <span style={{ color: "var(--gold-500)", marginLeft: 6 }}>· limite da oferta</span>
                ) : null}
              </span>
            </div>
          </div>
        ) : null}
      </Card>

      <Card>
        {/* Taxa mensal: nunca mais mostra fallback 1.79% fake. Se nao ha
            oferta ativa carregada (ofertasQ ainda em loading ou banco nao
            publicou tabela pra este convenio), exibe "—". Mesma coisa pra
            parcela / CET / total — o simulador so calcula com taxa real. */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Metric label="Sua parcela" valor={taxaAm ? fmtBRL(parcela) : "—"} accent danger={excedeMargem} />
          <Metric label="CET mensal" valor={taxaAm ? `${(taxaAm * 100).toFixed(2)}%` : "—"} />
          <Metric label="CET anual" valor={taxaAm ? `${(cetAa * 100).toFixed(2)}%` : "—"} />
          <Metric label="Total a pagar" valor={taxaAm ? fmtBRL(total) : "—"} />
        </div>
        {/* Duas razoes distintas pra nao haver taxa, e confundi-las manda o
            servidor pro lugar errado: sem tabela ele precisa esperar o banco;
            sem margem, ele mesmo pode resolver baixando o valor. */}
        {!taxaAm && !ofertasQ.isPending ? (
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--gold-500) 40%, transparent)", fontSize: 13, color: "var(--text-muted)" }}>
            {vitrine.length === 0
              ? "Nenhuma instituição publicou tabela de crédito pra este convênio ainda. Assim que uma tabela for cadastrada, a taxa aparece aqui."
              : <>Nenhuma instituição consegue {fmtBRL(valor)} dentro da sua margem de {fmtBRL(margemEmprestimo)}. Reduza o valor para até <b>{fmtBRL(maxValor)}</b>.</>}
          </div>
        ) : null}

        {excedeMargem ? (
          <div
            style={{
              marginTop: 18,
              padding: "12px 14px",
              borderRadius: 10,
              border: "1px solid color-mix(in srgb, var(--danger-500) 60%, transparent)",
              background: "color-mix(in srgb, var(--danger-500) 10%, transparent)",
              color: "var(--text)",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <b style={{ color: "var(--danger-500)" }}>Parcela acima da sua margem disponível.</b>
            <br />
            A parcela calculada ({fmtBRL(parcela)}) ultrapassa o limite de {fmtBRL(margemEmprestimo)} consignável.
            Reduza o valor ou aumente o número de parcelas.
          </div>
        ) : null}

        <div style={{ marginTop: 20, display: "flex", gap: 12 }}>
          <Button disabled={!podeSolicitar} onClick={solicitar}>
            Solicitar agora →
          </Button>
        </div>
        <p style={{ marginTop: 12, fontSize: ".78rem", color: "var(--text-dim)", margin: "12px 0 0" }}>
          Ao solicitar, sua margem fica travada por 48h enquanto a instituição analisa.
        </p>
      </Card>
        </>
      )}
    </div>
  );
}

/** Input controlado que aceita digitacao livre. Aplica clamp no blur / Enter
 *  (nao no meio da edicao — senao "1500" seria clampado pra "500" ao passar
 *  por "1" ou "15"). */
function ValorInput({
  valor,
  min,
  max,
  onChange,
}: {
  valor: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  const [texto, setTexto] = useState<string>(String(Math.round(valor)));
  useEffect(() => {
    setTexto(String(Math.round(valor)));
  }, [valor]);
  function commit(): void {
    const n = Number(texto.replace(/[^\d]/g, ""));
    if (!Number.isFinite(n) || n <= 0) {
      setTexto(String(Math.round(valor)));
      return;
    }
    const clamped = Math.max(min, Math.min(max, Math.round(n)));
    onChange(clamped);
    setTexto(String(clamped));
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600 }}>R$</span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); (e.target as HTMLInputElement).blur(); }
        }}
        aria-label="Valor solicitado em reais"
        style={{
          flex: 1,
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid var(--border-strong)",
          background: "var(--bg-elev-2)",
          color: "var(--text)",
          fontSize: 15,
          fontFamily: "inherit",
          fontVariantNumeric: "tabular-nums",
        }}
      />
    </div>
  );
}

function Metric({ label, valor, accent, danger }: { label: string; valor: string; accent?: boolean; danger?: boolean }) {
  const color = danger ? "var(--danger-500)" : accent ? "var(--emerald-500)" : "var(--text)";
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700, color }}>{valor}</div>
    </div>
  );
}

/** Simulador de cartao (consignado ou beneficio). Sem slider — a plataforma
 *  calcula UM limite proposto a partir da margem cartao (regra pragmatica:
 *  fatura minima ~2.5% do limite ≈ 5% do salario ⇒ limite = margem × 30).
 *  Mesma UX do app mobile (screenshot do cliente): servidor NAO escolhe o
 *  valor, so aceita o proposto ou nao. Ao solicitar, redireciona pra
 *  /servidor/solicitar-cartao (que faz a chamada final ao backend).
 */
function SimuladorCartao({ info, produto }: { info: MatriculaInfo | null; produto: "cartao_consignado" | "cartao_beneficio" }) {
  const nav = useNavigate();
  const lockProduto: LockProduto = produto === "cartao_beneficio" ? "CARTAO_BENEFICIOS" : "CARTAO_CONSIGNADO";
  const meta = produto === "cartao_consignado"
    ? {
        titulo: "Cartão de Crédito Consignado",
        subtitulo: "Cartão de crédito com fatura mínima descontada em folha. Você usa como um cartão normal — a fatura mínima sai do seu contracheque.",
        margemLabel: "Sua margem cartão consignado",
        tipoMargem: "CARTAO_CONSIGNADO" as const,
        cta: "Solicitar Cartão Consignado",
      }
    : {
        titulo: "Cartão Benefício Consignado",
        subtitulo: "Cartão restrito (farmácia, mercado, saúde) com fatura mínima descontada em folha. Limite exclusivo desses estabelecimentos.",
        margemLabel: "Sua margem cartão benefício",
        tipoMargem: "CARTAO_BENEFICIOS" as const,
        cta: "Solicitar Cartão Benefício",
      };

  const margemDisp = useMemo(() => {
    if (!info) return 0;
    const t = info.margem.margens_por_tipo.find((m) => m.tipo === meta.tipoMargem);
    return t?.disponivel ?? 0;
  }, [info, meta.tipoMargem]);

  const semMargem = margemDisp <= 0;
  // Limite proposto = margem × 30 (mesma regra do app mobile: R$600 ⇒ R$18.000).
  // Arredonda pra baixo em R$100 pra ficar bonito ("R$ 17.900" em vez de R$ 17.988).
  const limiteProposto = Math.max(0, Math.floor((margemDisp * 30) / 100) * 100);
  // Removido 24/07/2026: nao existe "banco default" — cartao consignado/beneficio
  // e' distribuido pelos bancos parceiros da prefeitura; qual banco atende
  // vem depois da analise. Textos usam expressao generica.

  // Lock derivado do backend + localStorage — mesma logica do SimuladorInline
  // (emprestimo). Trava fica ativa enquanto existir proposta pendente do
  // mesmo produto OU enquanto 48h nao expirar.
  const [clientLockExpiresAt, setClientLockExpiresAt] = useState<number | null>(() =>
    getActiveLock(info?.idMatricula ?? null, lockProduto),
  );
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    setClientLockExpiresAt(getActiveLock(info?.idMatricula ?? null, lockProduto));
    const onStorage = (e: StorageEvent) => {
      if (e.key === SIMULATION_LOCK_KEY) {
        setClientLockExpiresAt(getActiveLock(info?.idMatricula ?? null, lockProduto));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [info?.idMatricula, lockProduto]);

  const propostasQ = useQuery({
    queryKey: ["servidor", "propostas", info?.matricula ?? null],
    queryFn: () => atlas.servidor.propostas(info?.matricula),
    refetchInterval: 10_000,
    enabled: !!info,
  });

  const parseBRDate = (s: string | undefined | null): number | null => {
    if (!s) return null;
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
    if (!m) return null;
    const t = new Date(`${m[3]}-${m[2]}-${m[1]}T23:59:59`).getTime();
    return Number.isFinite(t) ? t : null;
  };

  const propostaPendente = useMemo(() => {
    const relevantes = (propostasQ.data?.propostas ?? [])
      .filter((p) => matchesLockProduto(p.tipoContrato, lockProduto, p.tipoMargem));
    return relevantes.find((p) => {
      const s = p.situacao.toLowerCase();
      const decidiu =
        s.includes("ativo") || s.includes("aprov") || s.includes("averb") || s.includes("formaliz") || s.includes("quitad") ||
        s.includes("cancel") || s.includes("recus") || s.includes("reprov") || s.includes("rejeit") || s.includes("negad") || s.includes("estorn") ||
        // Trava expirou (48h da reserva sem resposta do banco) -> proposta esta
        // encerrada, libera o servidor pra criar uma nova. Cliente 31/07/2026.
        s.includes("expir");
      return !decidiu;
    });
  }, [propostasQ.data, lockProduto]);

  const serverLockExpiresAt = useMemo(() => {
    if (!propostaPendente) return null;
    // Prefer ISO exatos (com hora/min/seg) — parse de DD/MM/YYYY perdia precisao
    // e caia em fim-de-dia (23:59:59), inflando a contagem em ate ~24h.
    const isoExp = propostaPendente.expira_em_iso ? Date.parse(propostaPendente.expira_em_iso) : NaN;
    if (Number.isFinite(isoExp)) return isoExp;
    const isoCreated = propostaPendente.criado_em_iso ? Date.parse(propostaPendente.criado_em_iso) : NaN;
    if (Number.isFinite(isoCreated)) return isoCreated + 48 * 60 * 60 * 1000;
    // Fallback pra propostas antigas sem ISO (dado persistido antes desta mudanca).
    const exp = parseBRDate(propostaPendente.expira_em);
    if (exp) return exp;
    const created = parseBRDate(propostaPendente.data);
    return created ? created + 48 * 60 * 60 * 1000 : null;
  }, [propostaPendente]);

  const lockExpiresAt = useMemo(() => {
    if (clientLockExpiresAt && serverLockExpiresAt) return Math.max(clientLockExpiresAt, serverLockExpiresAt);
    return clientLockExpiresAt ?? serverLockExpiresAt;
  }, [clientLockExpiresAt, serverLockExpiresAt]);

  useEffect(() => {
    if (!lockExpiresAt) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [lockExpiresAt]);

  useEffect(() => {
    if (!propostasQ.data || !clientLockExpiresAt) return;
    if (!propostaPendente) {
      const id = info?.idMatricula;
      if (id) clearLock(id, lockProduto);
      setClientLockExpiresAt(null);
    }
  }, [propostasQ.data, propostaPendente, clientLockExpiresAt, info?.idMatricula, lockProduto]);

  const locked = !!lockExpiresAt && lockExpiresAt > now;

  // QUEM OFERTA ESTE CARTAO na organizacao da matricula: os convenios que
  // declararam verba para o produto (regra do dominio, a mesma do app). Ate'
  // 10/09/2026 esta tela mandava o pedido sem banco e deixava a API
  // "decidir": com dois convenios ela recusava com "escolha a oferta de um
  // banco" numa tela que nao tinha como escolher. Um banco so' ja vem
  // selecionado; varios, o servidor escolhe; nenhum, a tela diz.
  const bancosQ = useQuery({
    queryKey: ["servidor", "bancos-do-produto", produto, info?.matricula],
    queryFn: () => atlas.servidor.bancosDoProduto(produto, info?.matricula),
    enabled: !!info,
  });
  const bancosDoProduto = bancosQ.data?.bancos ?? [];
  const [convenioEscolhido, setConvenioEscolhido] = useState<string | null>(null);
  const bancoEscolhido =
    bancosDoProduto.length === 1
      ? bancosDoProduto[0]!
      : (bancosDoProduto.find((b) => b.convenioId === convenioEscolhido) ?? null);
  const ninguemOferta = bancosQ.isSuccess && bancosDoProduto.length === 0;

  function solicitar() {
    if (semMargem || locked || !info || !bancoEscolhido) return;
    // Trava de 48h por produto — mesma logica do emprestimo (SimuladorInline).
    // Trava e SEPARADA da do emprestimo (margens/produtos independentes).
    setLock(info.idMatricula, lockProduto);
    setClientLockExpiresAt(Date.now() + 48 * 60 * 60 * 1000);
    // Vai pro Termo de Autorizacao (mesma UX do emprestimo — cliente pediu
    // "colocar o Termo tambem no cartao"). O tipo=cartao_consignado|cartao_beneficio
    // renderiza os detalhes especificos (limite proposto, sem parcelas/taxa)
    // e ao aceitar, o termo.tsx chama /me/cartoes (nao /me/propostas).
    const tipoTermo = produto === "cartao_beneficio" ? "cartao_beneficio" : "cartao_consignado";
    const params = new URLSearchParams({
      tipo: tipoTermo,
      limite: String(Math.round(limiteProposto)),
      // O banco escolhido acima. O termo mostra o nome e manda o convenio no
      // POST /me/cartoes — e' o que a API precisa para nao ter de "decidir".
      banco: bancoEscolhido.bancoNome,
      convenio: bancoEscolhido.convenioId,
    });
    nav(`/servidor/termo?${params.toString()}`);
  }

  if (locked && lockExpiresAt) {
    const restante = formatRemaining(lockExpiresAt - now);
    const expiraEmFormatado = new Date(lockExpiresAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    return (
      <Card style={{ padding: 28, textAlign: "center" }}>
        <div
          style={{
            width: 56, height: 56, borderRadius: "50%",
            background: "color-mix(in srgb, var(--gold-500) 20%, transparent)",
            color: "var(--gold-500)",
            display: "grid", placeItems: "center",
            fontSize: 28, margin: "0 auto 10px",
          }}
        >
          ⏳
        </div>
        <h3 style={{ margin: "0 0 6px" }}>{meta.titulo} em análise</h3>
        <p style={{ color: "var(--text-muted)", margin: 0, fontSize: ".9rem", maxWidth: 460, marginInline: "auto" }}>
          Você já tem uma solicitação de {meta.titulo.toLowerCase()} aguardando resposta da instituição. A margem fica travada até a instituição decidir ou até expirar em 48h.
        </p>
        <div
          style={{
            marginTop: 20, padding: "16px 20px", borderRadius: 12,
            background: "var(--bg-elev-2)", border: "1px solid var(--border-strong)",
            display: "inline-block", minWidth: 240,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            Tempo restante
          </div>
          <div style={{ marginTop: 4, fontSize: "2rem", fontWeight: 800, fontFamily: "var(--font-mono)", color: "var(--accent)", letterSpacing: "0.02em" }}>
            {restante}
          </div>
          <div style={{ marginTop: 4, fontSize: ".78rem", color: "var(--text-dim)" }}>
            Libera em {expiraEmFormatado}
          </div>
        </div>
        {/* Igual ao app: leva pra tela de Contratos, onde faz o acompanhamento. */}
        <div style={{ marginTop: 20 }}>
          <Button onClick={() => nav("/servidor/contratos")}>Acompanhar análise →</Button>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Descricao do produto — igual o cabecalho do app mobile. */}
      <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0, lineHeight: 1.5 }}>
        {meta.subtitulo}
      </p>

      {/* Card SUA MARGEM CARTAO */}
      <Card>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>
          {meta.margemLabel}
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: semMargem ? "var(--danger-500)" : "var(--emerald-500)", marginTop: 6 }}>
          {fmtBRL(margemDisp)}
        </div>
        <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "10px 0 0", lineHeight: 1.5 }}>
          Margem mensal para a fatura mínima. Fixada por regulação em 5% do salário líquido.
        </p>
      </Card>

      {semMargem ? (
        <Card style={{ borderColor: "var(--danger-500)" }}>
          <h3 style={{ marginTop: 0, color: "var(--danger-500)" }}>Sem margem disponível</h3>
          <p style={{ color: "var(--text-muted)", margin: 0, fontSize: 13 }}>
            Sua margem de {meta.titulo.toLowerCase()} está zerada nesta matrícula. Não é possível solicitar agora.
            Quite ou suspenda outro cartão do mesmo tipo pra liberar espaço.
          </p>
        </Card>
      ) : (
        <>
          {/* Card LIMITE PROPOSTO — sem slider, valor calculado. */}
          <Card>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>
              Limite proposto
            </div>
            <div style={{ fontSize: 34, fontWeight: 800, color: "var(--gold-500)", marginTop: 6, letterSpacing: "-0.01em" }}>
              {fmtBRL(limiteProposto)}
            </div>
            <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "10px 0 0", lineHeight: 1.5 }}>
              Estimado a partir da sua margem. A <b>instituição parceira</b> pode ajustar o limite após análise interna de crédito.
            </p>
          </Card>

          {/* QUAL BANCO. Um so' vem escolhido; varios, o servidor escolhe;
              nenhum, a tela diz e o botao nao aparece. */}
          <Card>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>
              Instituição emissora
            </div>
            {bancosQ.isLoading ? (
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 8 }}>Buscando as instituições que ofertam este cartão…</div>
            ) : ninguemOferta ? (
              <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "8px 0 0", lineHeight: 1.5 }}>
                Nenhuma instituição parceira oferta {meta.titulo.toLowerCase()} na sua organização ainda. Quando um
                convênio passar a ofertar, ele aparece aqui.
              </p>
            ) : bancosDoProduto.length === 1 ? (
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 8 }}>{bancosDoProduto[0]!.bancoNome}</div>
            ) : (
              <div role="radiogroup" aria-label="Escolha a instituição emissora" style={{ display: "grid", gap: 8, marginTop: 8 }}>
                {bancosDoProduto.map((b) => {
                  const on = b.convenioId === convenioEscolhido;
                  return (
                    <label
                      key={b.convenioId}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, cursor: "pointer",
                        border: `1px solid ${on ? "var(--gold-500)" : "var(--border)"}`,
                        background: on ? "color-mix(in srgb, var(--gold-500) 8%, transparent)" : "transparent",
                      }}
                    >
                      <input type="radio" name="banco-emissor" checked={on} onChange={() => setConvenioEscolhido(b.convenioId)} />
                      <span style={{ fontWeight: 600 }}>{b.bancoNome}</span>
                    </label>
                  );
                })}
                {!bancoEscolhido ? (
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Escolha uma instituição para continuar.</div>
                ) : null}
              </div>
            )}
          </Card>

          {/* Info box + CTA — mesmo tom do app. */}
          <div style={{
            padding: "14px 16px", borderRadius: 12,
            background: "var(--bg-elev-2)",
            border: "1px solid var(--border)",
            fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5,
          }}>
            Ao solicitar, o <b style={{ color: "var(--text)" }}>{bancoEscolhido?.bancoNome ?? "instituição escolhida"}</b> recebe seu pedido e entra em contato para emitir e ativar o cartão. A margem só é comprometida quando a instituição confirma — nada é descontado agora.
          </div>

          {!ninguemOferta ? (
            <Button onClick={solicitar} disabled={!bancoEscolhido || bancosQ.isLoading}>
              {meta.cta} →
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
